/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import { forwardRef, useEffect } from "react";
import { Theme, useTheme } from "@emotion/react";

import { Icon, Row, Typography } from "@/frameworks/designStuff/components";
import {
	buttonStyle,
	cursorStyle,
	sizeStyle,
	shapeStyle,
	typeStyle,
	buttonGroupStyle,
	iconSizeStyle,
	toolbarButtonStyle,
	loginButtonStyle,
	reactionButtonStyle
} from "./private/emotion";
import { ICON_SIZE_PALETTE, SHAPE_PALETTE, SIZE_PALETTE } from "./private/palette";
import { timer } from "rxjs";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	size?: DesignStuff.Button.SizeTypeProps;
	shape?: "circle" | "square" | "round";
	filled?:
		| "primary"
		| "secondary"
		| "tertiary"
		| "gradient"
		| "neutral"
		| "success"
		| "error"
		| "accent"
		| "neutral_secondary"
		| "transparent";
	variant?: "text" | "contained" | "outlined";
	typoColor?: keyof Colors;
	typoSize?: DesignStuff.Typography.TypoSizeType;
	startEnhancer?: React.ReactNode;
	endEnhancer?: React.ReactNode;
	activeAnimation?: boolean;
	children?: React.ReactNode;
}

/**
 * @param size 버튼 사이즈 ["mini" | "small" | "Xmedium" | "medium" | "large" | "Xlarge" | "XXlarge"]
 * @param shpae 버튼 모양 ["circle" | "square" | "round"]
 * @param variant 버튼 종류 ["text" | "contained" | "outlined"] / filled는 variant = "contained"에 만 적용되어있음
 * @param typoSize 텍스트 사이즈 ["Head1" | "Head2"	| "Head3" | "Head4" | "Head5" | "Head5_B" | "Head6" | "Title1" | "Title1_B" | "Title2" | "Title3" | "Body1" | "Body2" | "Body3" | "Body3_MD"| "Body4" | "Body5" | "Body5_SB" | "Body6" | "Body7" | "Button1" | "Button2" | "Button2_underline"]
 * @param filled 버튼 타입별 색상처리 ["primary" | "secondary" | "tertiary" | "gradient" | "neutral" | "success" | "error"]
 * @param startEnhancer 텍스트 앞 아이콘
 * @param endEnhancer 텍스트 뒤 아이콘
 * @param activeAnimation 클릭시 애니메이션 처리
 */

export const ButtonBase = forwardRef(
	(
		{
			activeAnimation = false,
			children,
			...rest
		}: { activeAnimation?: ButtonProps["activeAnimation"] } & React.ButtonHTMLAttributes<HTMLButtonElement>,
		ref: React.ForwardedRef<HTMLButtonElement>
	) => {
		const rippleEffect = (elm: HTMLElement) => {
			elm.addEventListener("click", function (e: MouseEvent) {
				const xInside = e.offsetX;
				const yInside = e.offsetY;
				const circle = document.createElement("span");

				circle.classList.add("circle");
				circle.style.top = yInside + "px";
				circle.style.left = xInside + "px";
				elm.appendChild(circle);

				timer(500).subscribe(() => circle.remove());
			});
		};

		useEffect(() => {
			if (!activeAnimation) return;
			if (!ref || !rippleEffect) return;

			// @ts-ignore
			const btnElm = ref.current as HTMLButtonElement;
			rippleEffect && rippleEffect(btnElm);
		}, []);

		return (
			<button ref={ref} css={[buttonStyle, cursorStyle]} {...rest}>
				{children}
			</button>
		);
	}
);

const ButtonComp = forwardRef(
	(
		{
			startEnhancer = null,
			endEnhancer = null,
			activeAnimation = false,
			variant = "contained",
			size = "medium",
			shape = "round",
			filled = "primary",
			typoSize,
			typoColor,
			children,
			...rest
		}: ButtonProps,
		ref: React.ForwardedRef<HTMLButtonElement>
	) => {
		const theme = useTheme();

		return (
			<ButtonBase
				ref={ref}
				activeAnimation={activeAnimation}
				css={[
					sizeStyle(SIZE_PALETTE[size].style),
					shapeStyle(SHAPE_PALETTE[shape]),
					typeStyle(theme, variant, filled, "neutral_80_100", typoColor)
				]}
				{...rest}
			>
				{startEnhancer && <Row.div>{startEnhancer}</Row.div>}
				<Typography.div
					typoColor={typoColor}
					typoSize={typoSize || (SIZE_PALETTE[size].fontType as DesignStuff.Typography.TypoSizeType)}
					className="buttonText"
				>
					{children}
				</Typography.div>
				{endEnhancer && <Row.div>{endEnhancer}</Row.div>}
			</ButtonBase>
		);
	}
);

export interface IconButtonProps extends Omit<ButtonProps, "size"> {
	name: DesignStuff.Icon.Name;
	size: DesignStuff.Button.IconSizeTypeProps;
	fill?: keyof Theme["colors"];
}

const IconButton = forwardRef(
	(
		{
			name,
			size,
			variant = "contained",
			filled = "secondary",
			shape = "circle",
			fill = "neutral_80_100",
			...rest
		}: IconButtonProps & React.HTMLAttributes<HTMLButtonElement>,
		ref: React.ForwardedRef<HTMLButtonElement>
	) => {
		const theme = useTheme();

		return (
			<ButtonBase
				ref={ref}
				activeAnimation={false}
				css={[
					shapeStyle(SHAPE_PALETTE[shape]),
					iconSizeStyle(ICON_SIZE_PALETTE[size].style),
					typeStyle(theme, variant, filled, fill)
				]}
				{...rest}
			>
				<Icon name={name} size={ICON_SIZE_PALETTE[size].iconSize} />
			</ButtonBase>
		);
	}
);

const ButtonGroup = forwardRef(
	({ children, ...rest }: React.HTMLAttributes<HTMLDivElement>, ref: React.ForwardedRef<HTMLDivElement>) => {
		return (
			<div ref={ref} css={buttonGroupStyle} {...rest}>
				{children}
			</div>
		);
	}
);

export interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	size?: "large"; // 추후 사이즈 생기면 추가
	active?: boolean;
}

const ToolbarButton = forwardRef(
	(
		{ size = "large", active = false, children, ...rest }: ToolbarButtonProps,
		ref: React.ForwardedRef<HTMLButtonElement>
	) => {
		const theme = useTheme();
		return (
			<ButtonBase ref={ref} activeAnimation={false} css={[toolbarButtonStyle(theme, size, active)]} {...rest}>
				{children}
			</ButtonBase>
		);
	}
);

export interface LoginButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	name: DesignStuff.Icon.Name;
}

const LoginButton = forwardRef(
	({ name, children, ...rest }: LoginButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
		const theme = useTheme();
		return (
			<ButtonBase ref={ref} activeAnimation={false} css={[loginButtonStyle(theme)]} {...rest}>
				{name && <Icon name={name} size={16} />}
				{children}
			</ButtonBase>
		);
	}
);

export interface ReactionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	name?: DesignStuff.Icon.Name;
}

const ReactionButton = forwardRef(
	({ name, children, ...rest }: ReactionButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
		const theme = useTheme();
		return (
			<ButtonBase ref={ref} css={[reactionButtonStyle(theme)]} {...rest}>
				{name && <Icon name={name} size={16} />}
				{children}
			</ButtonBase>
		);
	}
);

const WrapperButton = forwardRef(
	(
		{
			activeAnimation = false,
			variant = "contained",
			size = "medium",
			shape = "round",
			filled = "primary",
			children,
			...rest
		}: ButtonProps,
		ref: React.ForwardedRef<HTMLButtonElement>
	) => {
		const theme = useTheme();
		return (
			<ButtonBase
				ref={ref}
				activeAnimation={activeAnimation}
				css={[
					sizeStyle(SIZE_PALETTE[size].style),
					shapeStyle(SHAPE_PALETTE[shape]),
					typeStyle(theme, variant, filled)
				]}
				{...rest}
			>
				{children}
			</ButtonBase>
		);
	}
);
const TextOpenedButton = forwardRef(
	(
		{
			activeAnimation = false,
			variant = "text",
			shape = "round",
			filled = "transparent",
			children,
			...rest
		}: ButtonProps,
		ref: React.ForwardedRef<HTMLButtonElement>
	) => {
		const theme = useTheme();
		return (
			<ButtonBase
				ref={ref}
				activeAnimation={activeAnimation}
				css={[
					{ padding: "4px", minWidth: "88px" },
					shapeStyle(SHAPE_PALETTE[shape]),
					typeStyle(theme, variant, filled)
				]}
				{...rest}
			>
				{children}
			</ButtonBase>
		);
	}
);

interface IButtonProps extends React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement>> {
	Group: typeof ButtonGroup;
	Icon: typeof IconButton;
	Toolbar: typeof ToolbarButton;
	Login: typeof LoginButton;
	Reaction: typeof ReactionButton;
	Wrapper: typeof WrapperButton;
	TextOpened: typeof TextOpenedButton;
}

const Button = {
	...ButtonComp,
	Group: ButtonGroup,
	Icon: IconButton,
	Toolbar: ToolbarButton,
	Login: LoginButton,
	Reaction: ReactionButton,
	Wrapper: WrapperButton,
	TextOpened: TextOpenedButton
} as IButtonProps;

export default Button;
